/*
  Functions to generate a pbjs ad unit config. This build partner "bids" for
  each ad unit. See http://prebid.org/dev-docs/adunit-reference.html for more
  info.

  Bidders are:
  - AppNexus
  - Criteo
  - Gourmet Ads
  - Triple Lift
  - Index Exchange

  Each has their own id, and most of the partner ids are unique per placement
  and site.
*/
import {
  CATEGORY_BIDS,
  COLLECTION_BIDS,
  HOME_BIDS,
  POST_BIDS,
  VIDEO_BIDS,
  CRITEO_NETWORK_ID,
  TRIPLE_LIFT_DEFAULT_CODE,
  RUBICON_ACCOUNT_ID,
  RUBICON_SITE_ID,
  RUBICON_DEFAULT_ZONE_ID
} from 'constants/ads/prebid';

import {
  STANDARD_DESKTOP_SIZES,
  STANDARD_TABLET_SIZES,
  STANDARD_MOBILE_SIZES,
  RAIL_SIZES,
  STICKY_SIZES
} from 'constants/ads/sizes';

// Dedupe an array of arrays, e.g. [[320, 50], [320, 50]] becomes [[320, 50]]
function getUniqueArray(array) {
  return array.reduce((p, c) => {
    if (p.find(i => i[0] === c[0] && i[1] === c[1])) {
      return p;
    }
    return p.concat([c]);
  }, []);
}

const DEFAULT_LABELS = ['desktop', 'tablet', 'phone'];

const DEFAULT_SIZES = {
  desktop: STANDARD_DESKTOP_SIZES,
  tablet: STANDARD_TABLET_SIZES,
  phone: STANDARD_MOBILE_SIZES
};

const DEFAULT_MEDIA_TYPES = {
  banner: {
    sizes: getUniqueArray([
      ...DEFAULT_SIZES.desktop,
      ...DEFAULT_SIZES.tablet,
      ...DEFAULT_SIZES.phone
    ])
  }
};

const IN_POST_LABELS = ['post-small', 'post-large'];

const IN_POST_SIZES = {
  'post-small': STANDARD_MOBILE_SIZES,
  'post-large': [[728, 90]]
};

const IN_POST_MEDIA_TYPES = {
  banner: {
    sizes: getUniqueArray([
      ...IN_POST_SIZES['post-small'],
      ...IN_POST_SIZES['post-large']
    ])
  }
};

const VIDEO_SIZE = [640, 480];

// Add default bidders to config
const withDefaults = config => {
  const defaults = {
    criteoId: CRITEO_NETWORK_ID,
    tripleLiftId: TRIPLE_LIFT_DEFAULT_CODE,
    rubiconId: RUBICON_DEFAULT_ZONE_ID
  };
  return { ...defaults, ...config };
};

// Build Criteo bid
export function getCriteoBid(networkId) {
  return {
    bidder: 'criteo',
    params: {
      networkId
    }
  };
}

// Build Triple Lift bid
export function getTripleLiftBid(inventoryCode) {
  return {
    bidder: 'triplelift',
    params: {
      inventoryCode
    }
  };
}

// Build AppNexus bid
export function getAppNexusBid(placementId) {
  return {
    bidder: 'appnexus',
    params: {
      placementId
    }
  };
}

// Build Gourmet Ads bid
export function getGourmetAdsBid(placementId) {
  return {
    bidder: 'gourmetads',
    params: {
      placementId
    }
  };
}

export function getRubiconBid(zoneId) {
  return {
    bidder: 'rubicon',
    params: {
      accountId: RUBICON_ACCOUNT_ID,
      siteId: RUBICON_SITE_ID,
      zoneId
    }
  };
}

// Build Index Exchange bids. Note, unlike other bidders the the IX adapter
// requires a bid for each size.
export function getIxBids(siteId, sizes) {
  if (siteId && typeof siteId === 'object') {
    // siteId is an object with ids for each label
    return Object.entries(sizes).reduce(
      (p, [label, sizesForLabel]) =>
        p.concat(
          sizesForLabel.map(([width, height]) => ({
            bidder: 'ix',
            labelAny: [label],
            params: {
              // This is effectively a `placementId`
              siteId: `${siteId[label]}`,
              size: [width, height]
            }
          }))
        ),
      []
    );
  }
  // siteId is a string
  // Combine sizes for each label and get an array of unique values
  const unique = getUniqueArray(
    Object.values(sizes).reduce((p, c) => p.concat(c), [])
  );
  // Build bids
  return unique.map(([width, height]) => {
    // Find labels that match the size
    const labels = Object.entries(sizes).reduce((p, [label, sizesForLabel]) => {
      if (sizesForLabel.find(i => i[0] === width && i[1] === height)) {
        return p.concat(label);
      }
      return p;
    }, []);
    // Return the bid
    return {
      bidder: 'ix',
      labelAny: labels,
      params: {
        // This is effectively a `placementId`
        siteId: `${siteId}`,
        size: [width, height]
      }
    };
  }, []);
}

// Build an Index Exchange video bid
export function getIxVideoBid(siteId) {
  // See https://prebid.org/dev-docs/bidders/indexExchange.html#video for details
  return {
    bidder: 'ix',
    params: {
      siteId: `${siteId}`,
      size: VIDEO_SIZE,
      video: {
        mimes: [
          'video/mp4',
          'video/webm',
          // For VPAID
          'application/javascript',
          // m3u8
          'application/x-mpegURL',
          // mov
          'video/quicktime'
        ],
        minduration: 0,
        maxduration: 15,
        protocols: [2, 3, 5, 6], // "VAST 2.0",  "Vast 3.0", "VAST 2.0 Wrapper" and "VAST 3.0 Wrapper"
        api: [2] // VPAID 2
      }
    }
  };
}

export function getAllBidders(ops, sizes) {
  const {
    appNexusId,
    criteoId,
    tripleLiftId,
    gourmetAdsId,
    rubiconId,
    ixId
  } = withDefaults(ops);

  return [
    ...(appNexusId ? [getAppNexusBid(appNexusId)] : []),
    ...(criteoId ? [getCriteoBid(criteoId)] : []),
    ...(tripleLiftId ? [getTripleLiftBid(tripleLiftId)] : []),
    ...(gourmetAdsId ? [getGourmetAdsBid(gourmetAdsId)] : []),
    ...(rubiconId ? [getRubiconBid(rubiconId)] : []),
    ...(ixId ? getIxBids(ixId, sizes) : [])
  ];
}

function getDefaultConfig(code, ops) {
  return {
    labelAny: DEFAULT_LABELS,
    code,
    bids: getAllBidders(ops, DEFAULT_SIZES),
    mediaTypes: DEFAULT_MEDIA_TYPES
  };
}

function getInPostConfig(code, ops) {
  return {
    labelAny: IN_POST_LABELS,
    code,
    bids: getAllBidders(ops, IN_POST_SIZES),
    mediaTypes: IN_POST_MEDIA_TYPES
  };
}

function getVideoConfig(code, { path, appNexusId, ixId }) {
  return {
    labelAny: ['video'],
    code,
    bids: [
      ...(appNexusId ? [getAppNexusBid(appNexusId)] : []),
      ...(ixId ? [getIxVideoBid(ixId)] : [])
    ],
    mediaTypes: {
      video: {
        code: path,
        context: 'instream',
        playerSize: VIDEO_SIZE
      }
    }
  };
}

export const SIZE_CONFIG = [
  // Collection pages
  {
    mediaQuery: '(min-width: 1024px)',
    labels: ['desktop'],
    sizesSupported: DEFAULT_SIZES.desktop
  },
  {
    mediaQuery: '(min-width: 768px) and (max-width: 1023px)',
    labels: ['tablet'],
    sizesSupported: DEFAULT_SIZES.tablet
  },
  {
    mediaQuery: '(min-width: 0px) and (max-width: 727px)',
    labels: ['phone'],
    sizesSupported: DEFAULT_SIZES.phone
  },
  // Post pages
  {
    mediaQuery: '(min-width: 1440px)',
    labels: ['post-large'],
    sizesSupported: IN_POST_SIZES['post-large']
  },
  {
    mediaQuery: '(min-width: 0px) and (max-width: 1439px)',
    labels: ['post-small'],
    sizesSupported: IN_POST_SIZES['post-small']
  },
  {
    mediaQuery: '(min-width: 1024px)',
    labels: ['rail'],
    sizesSupported: RAIL_SIZES
  },
  {
    mediaQuery: '(min-width: 0px) and (max-width: 767px)',
    labels: ['sticky'],
    sizesSupported: STICKY_SIZES
  },
  // Video
  {
    mediaQuery: '(min-width: 0px)',
    labels: ['video'],
    sizesSupported: [[640, 480]]
  }
];

export const getAdUnitConfig = () => [
  // Build category, collection and home bids
  ...Object.entries({ ...CATEGORY_BIDS, ...COLLECTION_BIDS, ...HOME_BIDS }).map(
    ([key, ops]) => getDefaultConfig(key, ops)
  ),
  // Build post bids
  ...Object.entries(POST_BIDS).map(([key, ops]) => {
    switch (key) {
      case 'dfp-post-001-0':
      case 'dfp-footer-0':
        return getDefaultConfig(key, ops);
      case 'dfp-right-rail-0': {
        return {
          labelAny: ['rail'],
          code: 'dfp-right-rail-0',
          bids: getAllBidders(ops, {
            rail: RAIL_SIZES
          }),
          mediaTypes: {
            banner: {
              sizes: RAIL_SIZES
            }
          }
        };
      }
      case 'dfp-sticky-0': {
        return {
          labelAny: ['sticky'],
          code: 'dfp-sticky-0',
          bids: getAllBidders(ops, {
            sticky: STICKY_SIZES
          }),
          mediaTypes: {
            banner: {
              sizes: STICKY_SIZES
            }
          }
        };
      }
      default:
        return getInPostConfig(key, ops);
    }
  }),
  // Build video bids
  ...Object.entries(VIDEO_BIDS).map(([key, ops]) => getVideoConfig(key, ops))
];
